import React, { useState, useMemo } from 'react';
import { Footprints } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { stepsToCalories } from '../../lib/calculatorUtils';

const StepsToCaloriesCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [steps, setSteps] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [heightCm, setHeightCm] = useState('');

  const result = useMemo(() => {
    const s = parseInt(steps, 10);
    const w = parseFloat(weightKg);
    if (!s || !w || s < 100 || w < 20) return null;
    return stepsToCalories(s, w, parseFloat(heightCm) || 165);
  }, [steps, weightKg, heightCm]);

  const inputs = (
    <div className="space-y-5" data-testid="steps-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Walking Inputs</h2>
      <p className="text-slate-400 text-sm mb-5">Stride length is estimated as height × 0.415.</p>
      <NumberField id="steps-input" label="Steps Walked" value={steps} onChange={setSteps} placeholder="e.g. 10000" suffix="steps" min={100} max={100000} testId="steps-count-input" required />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Calories Burned" value={result.kcal.toLocaleString()} unit="kcal" accent="from-lime-500 to-emerald-500" testId="steps-result-kcal" />
      <div className="mt-8 space-y-1">
        <StatRow label="Distance walked (km)" value={result.distanceKm} unit="km" testId="steps-distance-km" />
        <StatRow label="Distance walked (mi)" value={result.distanceMi} unit="mi" testId="steps-distance-mi" />
        <StatRow label="Steps" value={parseInt(steps, 10).toLocaleString()} testId="steps-display" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Estimates assume an average walking pace. Faster pace and inclines burn more calories.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter steps and weight to see distance and calories burned." />
  );

  return (
    <CalculatorLayout
      slug="steps-to-calories" title="Steps to Calories Calculator"
      subtitle="Convert walking steps into distance and calories burned, personalised by your weight and stride."
      metaDescription="Steps to Calories Calculator to convert steps into distance walked and calories burned."
      icon={Footprints}
      accentGradient="from-lime-500 to-emerald-500" accentColor="text-lime-400"
      badgeBg="bg-lime-500/10" badgeBorder="border-lime-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Stride length:</strong> ≈ height × 0.415 (cm)</p>
        <p><strong className="text-slate-300">Calories:</strong> ≈ 0.04 kcal per step per kg body weight (relative to 70 kg)</p>
      </>}
    />
  );
};

export default StepsToCaloriesCalculatorPage;
